// MOO Policy Conservation Opportunities Report parser.
//
// The carrier hands you a save-the-policy worklist: every policy that
// just got a lapse notice, a returned-mail flag, or any other carrier-
// initiated retention event. We turn that into a triage queue inside
// the Retention tab.
//
// Why this matters financially: every policy issued in the last 12
// months sits inside the advance / chargeback window. If one lapses,
// the commission that was already advanced reverses. The conservation
// report tells you which advanced-commission policies are bleeding
// out *right now* while there's still time to save them. The parser's
// job is to surface that data structurally so the UI can prioritize.
//
// PDF text-extraction note: when you extract these reports via pdf.js
// the column-based layout collapses into a single string where the
// rightmost EVENT DESCRIPTION column gets interleaved with the
// CONTACT column on the left. We extract structured fields with
// regex first (CL#, policy, dates, premium mode — these are reliable
// because their formats are unique) and then classify the event type
// + extract sub-fields from the remaining text. Heuristic but robust
// across the formats we've seen.

const DATE_RE   = /\b(\d{2}\/\d{2}\/\d{4})\b/g;
const CL_RE     = /CL#:\s*(\d+)/i;
const POLICY_RE = /\b([A-Z][A-Z0-9]+)\/([A-Z]{1,3}\d+)\b/;     // e.g. IXEXP1/BU6415593
const PREM_RE   = /\b(\d{1,5}\.\d{2})-([A-Z]{2,4})\b/;          // e.g. 200.00-BSP
const PHONE_RE  = /PH#:\s*(\d{3}-\d{3}-\d{4})?/i;
const MONEY_RE  = /\$\s*([\d,]+\.\d{2})/;
// "received by 06/28/2026", "terminate effective 04/27/2026"
const RECEIVE_BY_RE = /received\s+by\s+(\d{2}\/\d{2}\/\d{4})/i;
const TERMINATE_RE  = /terminate(?:\s+effective)?\s+(\d{2}\/\d{2}\/\d{4})/i;

// Header detector — every record is preceded/followed by a long run of × characters.
const SEPARATOR_RE = /[×x]{10,}/g;

// Heuristic: products MOO lists. Used to find the boundary between
// the client name and the rest of the event text. Order matters —
// longer matches first.
const PRODUCT_NAMES = [
  'Index Universal Life',
  'Indexed Universal Life',
  'Universal Life',
  'Whole Life',
  'Term Life',
  'Final Expense Whole Life',
  'Final Expense',
  'Children\'s Whole Life',
  'Single Premium Whole Life',
];

function findProductName(blob) {
  for (const p of PRODUCT_NAMES) {
    const idx = blob.toLowerCase().indexOf(p.toLowerCase());
    if (idx >= 0) return { name: p, index: idx };
  }
  return null;
}

// US address tail: 1-2 trailing capitalized city words, optional
// comma, 2-letter state, ZIP. Anchored to require a capitalized FIRST
// city word. The `{0,1}` (max 2 city words total) prevents greedy
// consumption of street suffixes like "Shreibman Blvd" — the address
// line just before the city — into the city group. US cities are
// almost always 1-2 words; the rare 3-word names ("Saint Paul Lakes"
// type) are a tradeoff we accept here.
const CITY_STATE_ZIP_RE = /\b([A-Z][A-Za-z'.-]+(?:\s+[A-Z][A-Za-z'.-]+){0,1}),?\s+([A-Z]{2})\s+(\d{5})(?:-\d{4})?\b/g;

// Pull the report-run date out of the header.
//   "Policy Conservation Opportunities Report for 05/28/2026"
const RUN_DATE_RE = /Policy Conservation\s+Opportunit(?:y|ies)\s+Report(?:\s+for)?\s+(\d{2}\/\d{2}\/\d{4})/i;

// "AGENT: 1173602 - Harrison Roderick Curry"
const AGENT_RE = /AGENT:\s*(\d+)\s*-\s*([A-Za-z\s'-]+?)(?:\s{2,}|$|CONTACT|TYPE)/i;

// Classifies the event type from keywords. We deliberately keep the
// taxonomy small — the UI groups events by type and shows tailored
// actions for each.
function classifyEvent(text) {
  // CamelCase expansion first — pdf.js often glues description
  // fragments onto the next column ("LapsRobin", "The hCurtis").
  // Inserting a space at every lowerUpper boundary recovers the
  // word ("Laps Robin", "The h Curtis") so word-boundary regexes
  // below have a chance.
  const expanded = String(text).replace(/([a-z])([A-Z])/g, '$1 $2');
  if (/\blaps\b|lapse/i.test(expanded)) return 'lapse_warning';
  if (/returned mail|home office has received|received returned|\bveri(fy)?\b|\bnotify home\b|\bThe h(?:ome)?\b/i.test(expanded)) return 'returned_mail';
  if (/grace period|grace per/i.test(expanded)) return 'grace_period';
  if (/reinstate/i.test(expanded)) return 'reinstatement_opportunity';
  if (/\bnsf\b|non-sufficient|insufficient funds|nsf payment/i.test(expanded)) return 'nsf';
  return 'other';
}

function humanEventType(type) {
  return ({
    lapse_warning:            'Lapse warning',
    returned_mail:            'Returned mail',
    grace_period:             'In grace period',
    reinstatement_opportunity:'Reinstatement opportunity',
    nsf:                      'Payment failed (NSF)',
    other:                    'Conservation event',
  })[type] || 'Conservation event';
}

// Pull the client name out of a multi-line record by exploiting the
// visual row layout: the line immediately AFTER the PH#/CL# header
// row has the client name in the leftmost (CONTACT) column, followed
// by the product name in the middle column. extractPdfText's Y-line
// grouping preserves these row breaks, so when we have proper lines
// we can read the name directly instead of guessing.
//
// Returns the extracted name or '' if the structure doesn't look right.
function nameFromLineLayout(blob) {
  if (!blob || !blob.includes('\n')) return '';
  const lines = blob.split('\n').map(l => l.trim()).filter(Boolean);
  // Find the header row — the line with CL#: (and usually PH#:).
  const headerIdx = lines.findIndex(l => /CL#:/i.test(l));
  if (headerIdx < 0 || headerIdx + 1 >= lines.length) return '';
  const candidateLine = lines[headerIdx + 1];
  if (!candidateLine) return '';
  // The name sits at the start of the line; the product name follows.
  // We slice up to the product name (or to the first lowercase word,
  // which means we crossed into a description continuation column).
  const productInfo = findProductName(candidateLine);
  const head = productInfo ? candidateLine.slice(0, productInfo.index) : candidateLine;
  // First 2-3 capitalized words at the start of that slice = name.
  // Allow apostrophes / hyphens (O'Brien, Smith-Jones). Names like
  // "Ja Wan Davis" — 3 cap words — are still captured.
  const m = head.match(/^\s*([A-Z][A-Za-z'-]+(?:\s+[A-Z][A-Za-z'-]+){1,3})/);
  if (m) return m[1].trim();
  return '';
}

// Split a record blob into the structured fields. The blob is the
// concatenated text between two × separator runs.
function parseRecord(blob, defaults = {}) {
  // Line-aware name extraction runs FIRST against the unflattened
  // blob — it's the most reliable signal we have when extractPdfText
  // preserved line breaks. We stash the result and fall back to the
  // old heuristics below for the fragmented case (raw items.join(' ')
  // extracts that don't preserve rows).
  const lineLayoutName = nameFromLineLayout(blob);

  const compact = blob.replace(/\s+/g, ' ').trim();
  if (!compact || compact.length < 40) return null;
  // Ignore the page footer "Please consult your State or Federal Do Not Call list…"
  if (/^please consult/i.test(compact)) return null;
  // Ignore residual header rows.
  if (/^CONTACT\s+TYPE\/POLICY/i.test(compact)) return null;

  // Structured fields — these formats are unique enough that we can
  // pluck them out of the blob regardless of column interleave.
  const clMatch  = compact.match(CL_RE);
  if (!clMatch) return null;
  const polMatch = compact.match(POLICY_RE);
  const premMatch = compact.match(PREM_RE);
  const phoneMatch = compact.match(PHONE_RE);

  // Three dates in sequence: issue, paid-to, event. We grab the first
  // three MM/DD/YYYY tokens; subsequent dates are inside the event
  // description (termination, pay-by) and are pulled out separately.
  const allDates = [...compact.matchAll(DATE_RE)].map(m => m[1]);

  // Event subfields (lapse-specific). Pulled by keyword match so they
  // work even when text positioning shifts between records.
  const moneyMatch = compact.match(MONEY_RE);
  const receiveByMatch = compact.match(RECEIVE_BY_RE);
  const terminateMatch = compact.match(TERMINATE_RE);

  // Product name — used as the boundary marker between the client name
  // (to the left of it in the visual layout, which means earlier in
  // the text stream after we strip the structured fields) and the
  // event description.
  const productInfo = findProductName(compact);

  // Address — find every city/state/zip candidate, pick the rightmost
  // (= visually the last address tail in the blob, which is the real
  // address; earlier matches inside description text would only appear
  // if a description happened to mention a city/state pair).
  const cszMatches = [...compact.matchAll(CITY_STATE_ZIP_RE)];
  const cszMatch = cszMatches.length ? cszMatches[cszMatches.length - 1] : null;
  let addressLine1 = '';
  let city = '', state = '', zip = '';
  if (cszMatch) {
    city  = cszMatch[1].trim();
    state = cszMatch[2];
    zip   = cszMatch[3];
    // The street typically appears just before the city. Find the
    // LAST sequence in the segment that starts with a digit (street #)
    // and runs to the city-state-zip boundary. Use a global match on
    // a generous pattern, then take the last hit — that's the one
    // immediately adjacent to the city-state-zip in the visual layout.
    const beforeCsz = compact.slice(0, cszMatch.index).trim();
    const streetMatches = [...beforeCsz.matchAll(/(\d+\s+[A-Z][A-Za-z0-9.'\s-]+?(?=\s+[A-Z]{2,}|\s+[a-z]{2,})|\d+\s+[A-Z][A-Za-z0-9.'\s-]{2,40})/g)];
    if (streetMatches.length) {
      const last = streetMatches[streetMatches.length - 1][1];
      // Trim any trailing description-vocabulary tokens.
      addressLine1 = last
        .replace(/\s+(information|policy|verify|customer|phone|displayed|business|effective|terminate|received|or the|home office|please).*$/i, '')
        .trim();
    }
  }

  // Client name — fallible. The text between the premium mode and the
  // product name is an INTERLEAVE of (a) end of one event-description
  // sentence and (b) the client name. Strategy:
  //   1. Walk that segment and pick capitalized word runs.
  //   2. Reject runs containing event-description vocabulary
  //      ("Please", "Lapse", "Minimum", product/insurance terms).
  //   3. Take the LAST surviving run before the product name, since
  //      the name visually sits adjacent to the product.
  // This is robust to both layouts we've seen (lapse_warning and
  // returned_mail) without needing the actual column coordinates.
  const NAME_BLOCKLIST = new Set([
    'please','please consult','the','for','this','customer','minimum','minimum payment',
    'lapse','laps','notice','sent','flexible','universal','life','whole','term',
    'index','indexed','final','expense','children','single','premium','received',
    'must','policy','terminate','effective','home','office','returned','mail',
    'verify','address','phone','number','notify','displayed','information',
    'business','reinstatement','grace','period','application','process',
  ]);
  function isProbablyName(s) {
    const tokens = s.toLowerCase().split(/\s+/);
    if (tokens.length < 2) return false;
    for (const t of tokens) {
      // any blocklisted word disqualifies the candidate
      if (NAME_BLOCKLIST.has(t)) return false;
    }
    return true;
  }
  // ── Token splitter for run-on fragments ──────────────────────────
  // pdf.js text-content extraction often glues fragments together
  // without whitespace because the underlying glyph runs were on the
  // same line (e.g. "LapsRobin" = "Laps" + "Robin", "The hCurtis" =
  // "The h" + "Curtis"). We split CamelCase joins so the name
  // extractor can see "Robin" cleanly, and we strip short
  // lowercase-prefix fragments that look like truncated event verbs.
  function splitCamelTokens(tokens) {
    const out = [];
    for (const tok of tokens) {
      // Split at "lowerUpper" boundaries — "LapsRobin" → ["Laps","Robin"]
      const parts = tok.split(/(?<=[a-z])(?=[A-Z])/);
      for (const p of parts) out.push(p);
    }
    return out;
  }

  // Line-layout extraction wins when we have it. Robin Bonner / Curtis
  // Field both land here on real PDFs because extractPdfText preserves
  // the row breaks. The heuristic fallback below only fires if line
  // structure was lost (pasted text, raw items.join extracts, etc).
  let clientName = lineLayoutName;
  if (!clientName && premMatch && productInfo) {
    const start = premMatch.index + premMatch[0].length;
    const end   = productInfo.index;
    if (end > start) {
      const between = compact.slice(start, end).trim();
      const rawTokens = between.split(/\s+/);
      const tokens = splitCamelTokens(rawTokens);
      const cleanWindows = [];
      const isCapped = (w) => /^[A-Z][A-Za-z'-]+$/.test(w);
      for (let i = 0; i < tokens.length; i++) {
        for (let len = 2; len <= 3; len++) {
          if (i + len > tokens.length) break;
          const window = tokens.slice(i, i + len);
          if (!window.every(isCapped)) continue;
          const s = window.join(' ');
          if (!isProbablyName(s)) continue;
          cleanWindows.push({ s, end: i + len });
        }
      }
      if (cleanWindows.length) {
        // The window that ends LAST (visually closest to the product)
        // is typically the name. Tie-break: prefer 2-word over 3-word
        // so we don't accidentally absorb a leading description word.
        cleanWindows.sort((a, b) => b.end - a.end || a.s.length - b.s.length);
        clientName = cleanWindows[0].s;
      }
    }
  }
  // Last-ditch fallback: scan the WHOLE blob (after stripping the
  // structured fields) for a 2-word capitalized run that survives the
  // name-blocklist. Helps when the product-name boundary isn't where
  // we expected (e.g. some carriers use unusual product wording).
  if (!clientName) {
    const stripped = splitCamelTokens(compact.split(/\s+/));
    for (let i = 0; i < stripped.length - 1; i++) {
      const w = stripped.slice(i, i + 2);
      if (!w.every(t => /^[A-Z][A-Za-z'-]+$/.test(t))) continue;
      if (!isProbablyName(w.join(' '))) continue;
      // Skip city/state pairs that look like names (e.g. "La Vergne").
      if (cszMatch && cszMatch[1].includes(w[0])) continue;
      clientName = w.join(' ');
      break;
    }
  }

  // Event description — reconstruct it from the leftover characters.
  // We try a few markers known to start description sentences and
  // join them with ellipsis. Imperfect but readable in the UI; the
  // structured subfields above carry the actionable values.
  let eventDescription = '';
  const descMarkers = [
    /Lapse notice[^.]*\.[^×]*?(?=$|×|Please consult)/i,
    /home office has received returned mail[^.]*\.[^×]*?(?=$|×|Please consult)/i,
    /grace period[^.]*\.[^×]*?(?=$|×|Please consult)/i,
    /reinstatement[^.]*\.[^×]*?(?=$|×|Please consult)/i,
  ];
  for (const re of descMarkers) {
    const m = compact.match(re);
    if (m) { eventDescription = m[0].trim(); break; }
  }
  if (!eventDescription) {
    // Fallback: take everything after the address as the description.
    if (cszMatch) {
      eventDescription = compact.slice(cszMatch.index + cszMatch[0].length).trim();
    } else if (productInfo) {
      eventDescription = compact.slice(productInfo.index + productInfo.name.length).trim();
    }
  }

  const eventType = classifyEvent(compact);

  return {
    // Identifiers
    clientNum: clMatch[1],
    policyCode: polMatch ? polMatch[1] : null,
    policyNum:  polMatch ? polMatch[2] : null,
    // Contact
    clientName,
    phone: (phoneMatch && phoneMatch[1]) || '',
    addressLine1,
    city, state, zip,
    // Policy facts
    productName: productInfo ? productInfo.name : '',
    issueDate: allDates[0] || null,
    paidToDate: allDates[1] || null,
    eventDate: allDates[2] || null,
    modalPremium: premMatch ? Number(premMatch[1]) : null,
    billingMethod: premMatch ? premMatch[2] : null,
    // Event
    eventType,
    eventTypeLabel: humanEventType(eventType),
    eventDescription,
    // Lapse-specific subfields (null when not applicable)
    minPayment:     moneyMatch    ? Number(moneyMatch[1].replace(/,/g, '')) : null,
    payByDate:      receiveByMatch ? receiveByMatch[1] : null,
    terminationDate: terminateMatch ? terminateMatch[1] : null,
    // Bookkeeping
    reportRunDate: defaults.reportRunDate || null,
    agentProdNum:  defaults.agentProdNum  || null,
    carrier: 'MOO',
    rawBlob: compact,
  };
}

// Carrier detection heuristic. Drives the dispatcher below. New
// carriers (Americo, Transamerica) get added when we have sample
// reports in hand to build their parser.
function detectCarrier(rawText) {
  const t = rawText.toLowerCase();
  if (t.includes('mutual of omaha') || t.includes('moo'))  return 'MOO';
  if (t.includes('americo'))                                return 'AMERICO';
  if (t.includes('transamerica'))                           return 'TRANSAMERICA';
  return 'UNKNOWN';
}

// Top-level entry point. Detects the carrier, dispatches to the right
// parser, returns a normalized { events, header }. Today only MOO has
// a real parser; the other carriers return a stub with an error
// message so the UI can surface "send us a sample of this format" to
// the operator.
export function parseConservationReport(rawText) {
  if (!rawText || typeof rawText !== 'string') return { events: [], header: {}, carrierSupported: false, carrier: 'UNKNOWN' };

  const carrier = detectCarrier(rawText);

  if (carrier === 'MOO') {
    return parseMooConservation(rawText);
  }
  // Stub return for carriers we haven't built parsers for yet. The
  // RetentionView surfaces this via a toast so the operator knows
  // we received the report but need a sample to build the parser.
  return {
    events: [],
    header: { carrier, reportRunDate: null, agentProdNum: null, agentName: null },
    carrierSupported: false,
    carrier,
    unsupportedMessage: `${carrier} conservation report format isn't supported yet. Forward a sample PDF to support so we can build the parser.`,
  };
}

// MOO-specific parser. Extracted so the dispatcher above stays clean
// and so future carriers can mirror this shape: parseAmericoConservation,
// parseTransamericaConservation, etc., each owning their own field-
// extraction quirks.
function parseMooConservation(rawText) {
  // Pull header fields once.
  const runDateMatch = rawText.match(RUN_DATE_RE);
  const agentMatch   = rawText.match(AGENT_RE);
  const header = {
    reportRunDate: runDateMatch ? runDateMatch[1] : null,
    agentProdNum:  agentMatch   ? agentMatch[1]    : null,
    agentName:     agentMatch   ? agentMatch[2].trim() : null,
    carrier: 'MOO',
  };

  // Split on the × separator runs. Each chunk between separators is a
  // candidate record blob. The first chunk is the header (no CL#); the
  // last chunk is the footer (Do-Not-Call disclaimer). parseRecord()
  // returns null for both so we filter them out.
  const chunks = rawText.split(SEPARATOR_RE);
  const events = chunks
    .map(chunk => parseRecord(chunk, header))
    .filter(Boolean);

  return { events, header, carrierSupported: true, carrier: 'MOO' };
}

// ── Risk helpers ────────────────────────────────────────────────────
// Stable id derived from (carrier, policyNum, eventDate) so a re-upload
// of the same report doesn't duplicate the row.
export function conservationEventId(ev) {
  return `${ev.carrier || 'MOO'}|${ev.policyNum || ev.clientNum || 'unknown'}|${ev.eventDate || ''}|${ev.eventType}`;
}

// Days until the policy terminates. Negative = already past the
// effective termination date (still recoverable but the meter ran out).
export function daysUntilTermination(ev, today = new Date()) {
  if (!ev.terminationDate) return null;
  const [m, d, y] = ev.terminationDate.split('/').map(Number);
  if (!m || !d || !y) return null;
  const t = new Date(y, m - 1, d);
  const diff = Math.ceil((t.getTime() - today.getTime()) / (24 * 3600 * 1000));
  return diff;
}

// Days until the cure window closes (pay-by date). This is usually
// LATER than the termination date for UL because UL reverts the
// termination effective date once the cure window expires.
export function daysUntilCureWindowCloses(ev, today = new Date()) {
  if (!ev.payByDate) return null;
  const [m, d, y] = ev.payByDate.split('/').map(Number);
  if (!m || !d || !y) return null;
  const t = new Date(y, m - 1, d);
  const diff = Math.ceil((t.getTime() - today.getTime()) / (24 * 3600 * 1000));
  return diff;
}

// Is the policy inside the carrier's advance / chargeback window?
// Default = 12 months from issue date (matches MOO's typical 12-month
// advance schedule). Tighter or wider windows can be configured later.
export function isInChargebackWindow(ev, today = new Date(), monthsWindow = 12) {
  if (!ev.issueDate) return false;
  const [m, d, y] = ev.issueDate.split('/').map(Number);
  if (!m || !d || !y) return false;
  const issued = new Date(y, m - 1, d);
  const cutoff = new Date(issued);
  cutoff.setMonth(cutoff.getMonth() + monthsWindow);
  return today < cutoff;
}

// Three-tier priority. Drives both the sort order and the color
// banding in the UI.
//   critical → lapse_warning + in chargeback window (real $ at risk)
//   high     → lapse_warning outside chargeback window
//   medium   → returned_mail / grace_period / NSF (slow-motion lapse risk)
//   low      → everything else
export function conservationPriority(ev, today = new Date()) {
  if (ev.eventType === 'lapse_warning' && isInChargebackWindow(ev, today)) return 'critical';
  if (ev.eventType === 'lapse_warning') return 'high';
  if (ev.eventType === 'nsf' || ev.eventType === 'grace_period') return 'medium';
  if (ev.eventType === 'returned_mail') return 'medium';
  return 'low';
}
